import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSliders, faGaugeHigh, faShuffle } from '@fortawesome/free-solid-svg-icons';

import { assessmentApi } from '../services/api';
import { PageHeader, Card, Button } from '../components/ui';

/*
|--------------------------------------------------------------------------
| Configuration Page
|--------------------------------------------------------------------------
| Central place to edit the two backend-driven scoring configs also
| surfaced (read-only-ish, edit-in-place) inside the Assess page's
| Assessment Information modal:
|   - Page Complexity factor weights + Low/Medium/High thresholds
|     (GET/PUT /assessment/page-complexity-config)
|   - Pairwise Detail dimension weights used by the merge/similarity
|     algorithm (GET/PUT /assessment/weights)
| Both configs are otherwise only reachable from deep inside Assess.js;
| this page gives them a permanent home under Config > Configuration.
|--------------------------------------------------------------------------
*/

const DEFAULT_PAGE_COMPLEXITY_CONFIG = {
    weights: {
        cards: 2,
        datasets: 2,
        variables: 1,
        sets: 1,
        groups: 1,
        hierarchy: 1,
        appLinkedPages: 1.5
    },
    thresholds: {
        high: 20,
        medium: 8
    }
};

const PAGE_COMPLEXITY_FACTOR_META = [
    { key: 'cards', label: 'Cards', description: 'Weight per unique card used on the page.' },
    { key: 'datasets', label: 'Datasets', description: 'Weight per unique dataset used on the page.' },
    { key: 'variables', label: 'Variables', description: 'Weight per variable defined across the page\'s cards.' },
    { key: 'sets', label: 'Sets', description: 'Weight per set/filter-group used across the page\'s cards.' },
    { key: 'groups', label: 'Groups', description: 'Weight per grouping definition used across the page\'s cards.' },
    { key: 'hierarchy', label: 'Hierarchy', description: 'Weight per hierarchy definition used across the page\'s cards.' },
    { key: 'appLinkedPages', label: 'Other pages in same app', description: 'Weight per other page that shares the same Domo app as this page.' }
];

const DEFAULT_PAIRWISE_WEIGHTS = {
    datasources: 0.20,
    sourceTables: 0.13,
    connectorType: 0.05,
    worksheets: 0.14,
    chartTypes: 0.05,
    fieldBindings: 0.07,
    columns: 0.12,
    columnTypes: 0.04,
    calculatedFields: 0.08,
    formulaSignatures: 0.04,
    dataflows: 0.04,
    variables: 0.01,
    sets: 0.01,
    groups: 0.01,
    hierarchy: 0.01
};

const PAIRWISE_DIMENSION_META = [
    { key: 'datasources', label: 'Datasources', description: 'Jaccard similarity of datasource IDs shared between two pages. Strongest merge signal.' },
    { key: 'sourceTables', label: 'Source tables', description: 'Jaccard similarity of physical table names from Domo stream configuration.' },
    { key: 'connectorType', label: 'Connector type', description: 'Jaccard similarity of data provider/driver type (e.g., PostgreSQL, Snowflake).' },
    { key: 'worksheets', label: 'Worksheets / Cards', description: 'Jaccard similarity of normalized card/worksheet names on each page.' },
    { key: 'chartTypes', label: 'Chart types', description: 'Jaccard similarity of visualization types (bar, line, pie, etc.).' },
    { key: 'fieldBindings', label: 'Field bindings', description: 'Jaccard similarity of axis column bindings (category + value fields).' },
    { key: 'columns', label: 'Columns', description: 'Jaccard similarity of column names from datasets used by each page.' },
    { key: 'columnTypes', label: 'Column types', description: 'Jaccard similarity of data types (STRING, DECIMAL, DATETIME, etc.).' },
    { key: 'calculatedFields', label: 'Calculated fields', description: 'Jaccard similarity of Beast Mode / calculated field names.' },
    { key: 'formulaSignatures', label: 'Formula signatures', description: 'Jaccard similarity of function calls extracted from formulas (SUM, AVG, etc.).' },
    { key: 'dataflows', label: 'Dataflows', description: 'Jaccard similarity of associated dataflows / ETL pipelines.' },
    { key: 'variables', label: 'Variables', description: 'Jaccard similarity of variable names defined on each page.' },
    { key: 'sets', label: 'Sets', description: 'Jaccard similarity of set/filter-group names used on each page.' },
    { key: 'groups', label: 'Groups', description: 'Jaccard similarity of grouping definitions used on each page.' },
    { key: 'hierarchy', label: 'Hierarchy', description: 'Jaccard similarity of hierarchy definitions used on each page.' }
];

const TABS = [
    { id: 'page-complexity', label: 'Page Complexity', icon: faGaugeHigh },
    { id: 'pairwise-detail', label: 'Pairwise Detail', icon: faShuffle }
];

export default function ConfigurationPage() {
    const [activeTab, setActiveTab] = useState('page-complexity');

    return (
        <div style={{ padding: 24 }} className="animate-fade-in">

            <PageHeader
                title="Configuration"
                subtitle="Tune the scoring rules the Assessment engine uses for Page Complexity and Pairwise similarity."
            />

            <div style={{ display: 'flex', borderBottom: '0.5px solid var(--border)', marginBottom: 20 }}>
                {TABS.map(tab => (
                    <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 7,
                            padding: '10px 16px',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: activeTab === tab.id ? 600 : 500,
                            color: activeTab === tab.id ? 'var(--accent)' : 'var(--text-secondary)',
                            borderBottom: activeTab === tab.id ? '2px solid var(--accent)' : '2px solid transparent',
                            marginBottom: -0.5
                        }}
                    >
                        <FontAwesomeIcon icon={tab.icon} />
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'page-complexity' ? <PageComplexityConfigCard /> : <PairwiseWeightsConfigCard />}

        </div>
    );
}

/*
|--------------------------------------------------------------------------
| Page Complexity Config
|--------------------------------------------------------------------------
*/

function PageComplexityConfigCard() {

    const [config, setConfig] = useState(DEFAULT_PAGE_COMPLEXITY_CONFIG);
    const [draft, setDraft] = useState(DEFAULT_PAGE_COMPLEXITY_CONFIG);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setLoading(true);
            setError(null);
            try {
                const resp = await assessmentApi.getPageComplexityConfig();
                const data = resp.data?.data || resp.data || null;
                if (cancelled || !data) return;

                const merged = {
                    weights: { ...DEFAULT_PAGE_COMPLEXITY_CONFIG.weights, ...(data.weights || {}) },
                    thresholds: { ...DEFAULT_PAGE_COMPLEXITY_CONFIG.thresholds, ...(data.thresholds || {}) }
                };
                setConfig(merged);
                setDraft(merged);
            } catch (err) {
                if (!cancelled) {
                    setError(err.response?.data?.error || err.message || 'Failed to load page complexity config, showing defaults.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, []);

    const handleWeightChange = (key, value) => {
        const numeric = value === '' ? '' : Number(value);
        setDraft(prev => ({ ...prev, weights: { ...prev.weights, [key]: numeric } }));
    };

    const handleThresholdChange = (key, value) => {
        const numeric = value === '' ? '' : Number(value);
        setDraft(prev => ({ ...prev, thresholds: { ...prev.thresholds, [key]: numeric } }));
    };

    const handleStartEditing = () => {
        setDraft(config);
        setEditing(true);
    };

    const handleCancelEditing = () => {
        setDraft(config);
        setEditing(false);
    };

    const handleSave = async () => {

        const weights = {};
        for (const factor of PAGE_COMPLEXITY_FACTOR_META) {
            const value = Number(draft.weights?.[factor.key]);
            if (Number.isNaN(value) || value < 0) {
                toast.error(`${factor.label} weight must be a non-negative number.`);
                return;
            }
            weights[factor.key] = value;
        }

        const highThreshold = Number(draft.thresholds?.high);
        const mediumThreshold = Number(draft.thresholds?.medium);

        if (Number.isNaN(highThreshold) || Number.isNaN(mediumThreshold) || highThreshold < 0 || mediumThreshold < 0) {
            toast.error('High and Medium thresholds must be non-negative numbers.');
            return;
        }

        if (mediumThreshold >= highThreshold) {
            toast.error('The Medium threshold must be lower than the High threshold.');
            return;
        }

        const normalized = { weights, thresholds: { high: highThreshold, medium: mediumThreshold } };

        setSaving(true);
        try {
            await assessmentApi.updatePageComplexityConfig(normalized);
            setConfig(normalized);
            setDraft(normalized);
            setEditing(false);
            toast.success('Page complexity settings updated.');
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || 'Failed to save page complexity settings.');
        } finally {
            setSaving(false);
        }

    };

    return (
        <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 4 }}>
                <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FontAwesomeIcon icon={faSliders} style={{ color: 'var(--accent)' }} />
                        Page Complexity — Factor Weights &amp; Thresholds
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                        Controls the Low/Medium/High complexity shown on the Pages tab and rolled up into Overall Complexity and Migration Waves.
                    </div>
                </div>

                {!loading && (
                    editing ? (
                        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                            <Button variant="secondary" size="sm" onClick={handleCancelEditing} disabled={saving}>Cancel</Button>
                            <Button variant="primary" size="sm" onClick={handleSave} loading={saving}>Save</Button>
                        </div>
                    ) : (
                        <Button variant="secondary" size="sm" onClick={handleStartEditing}>Edit Complexity</Button>
                    )
                )}
            </div>

            {error && (
                <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 10 }}>{error}</div>
            )}

            <div style={{ overflowX: 'auto', border: '0.5px solid var(--border)', borderRadius: 'var(--radius-md)', marginTop: 16, marginBottom: 14 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                        <tr>
                            {['Factor', 'Weight', 'How it works'].map(h => (
                                <th key={h} style={configTableHeaderStyle}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={3} style={{ ...configTableCellStyle, textAlign: 'center', color: 'var(--text-tertiary)' }}>
                                    Loading...
                                </td>
                            </tr>
                        ) : PAGE_COMPLEXITY_FACTOR_META.map(factor => (
                            <tr key={factor.key}>
                                <td style={configTableCellStyle}>{factor.label}</td>
                                <td style={{ ...configTableCellStyle, whiteSpace: 'nowrap' }}>
                                    {editing ? (
                                        <input
                                            type="number"
                                            min={0}
                                            step={0.1}
                                            value={draft.weights?.[factor.key]}
                                            onChange={e => handleWeightChange(factor.key, e.target.value)}
                                            style={configInputStyle}
                                        />
                                    ) : (
                                        <span style={{ fontWeight: 600 }}>{Number(config.weights?.[factor.key] ?? 0)}</span>
                                    )}
                                </td>
                                <td style={configTableCellStyle}>{factor.description}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <ThresholdBox
                    label="High threshold (score ≥)"
                    value={editing ? draft.thresholds?.high : config.thresholds?.high}
                    editing={editing}
                    color="var(--danger)"
                    onChange={v => handleThresholdChange('high', v)}
                />
                <ThresholdBox
                    label="Medium threshold (score ≥)"
                    value={editing ? draft.thresholds?.medium : config.thresholds?.medium}
                    editing={editing}
                    color="var(--warning-text)"
                    onChange={v => handleThresholdChange('medium', v)}
                />
                <ThresholdBox
                    label="Low threshold (score <)"
                    value={editing ? draft.thresholds?.medium : config.thresholds?.medium}
                    editing={false}
                    color="var(--success-text)"
                    onChange={v => handleThresholdChange('medium', v)}
                />
            </div>

            <div style={{ fontSize: 11.5, lineHeight: 1.6, color: 'var(--text-secondary)', marginTop: 14 }}>
                If score &ge; High threshold, the page is <strong>High</strong> complexity;
                if score &ge; Medium threshold, it's <strong>Medium</strong>; otherwise <strong>Low</strong>.
            </div>
        </Card>
    );
}

function ThresholdBox({ label, value, editing, color, onChange }) {
    return (
        <div style={{ flex: '1 1 200px', border: '0.5px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 5 }}>{label}</div>
            {editing ? (
                <input
                    type="number"
                    min={0}
                    step={1}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    style={{ ...configInputStyle, width: 90, fontSize: 13 }}
                />
            ) : (
                <div style={{ fontSize: 16, fontWeight: 700, color }}>{value}</div>
            )}
        </div>
    );
}

/*
|--------------------------------------------------------------------------
| Pairwise Weights Config
|--------------------------------------------------------------------------
*/

function PairwiseWeightsConfigCard() {

    const [weights, setWeights] = useState(DEFAULT_PAIRWISE_WEIGHTS);
    const [draft, setDraft] = useState(DEFAULT_PAIRWISE_WEIGHTS);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setLoading(true);
            setError(null);
            try {
                const resp = await assessmentApi.getPairwiseWeights();
                const data = resp.data?.data || resp.data || {};
                if (cancelled) return;
                const merged = { ...DEFAULT_PAIRWISE_WEIGHTS, ...data };
                setWeights(merged);
                setDraft(merged);
            } catch (err) {
                if (!cancelled) {
                    setError(err.response?.data?.error || err.message || 'Failed to load weights, showing defaults.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, []);

    const weightsSum = Object.values(draft).reduce((sum, v) => sum + (Number(v) || 0), 0);
    const weightsSumRounded = Math.round(weightsSum * 1000) / 1000;
    const weightsExceedOne = weightsSumRounded > 1;

    const handleWeightChange = (key, value) => {
        const numeric = value === '' ? '' : Number(value);
        setDraft(prev => ({ ...prev, [key]: numeric }));
    };

    const handleStartEditing = () => {
        setDraft(weights);
        setEditing(true);
    };

    const handleCancelEditing = () => {
        setDraft(weights);
        setEditing(false);
    };

    const handleSave = async () => {

        const normalized = {};
        for (const dim of PAIRWISE_DIMENSION_META) {
            const value = Number(draft[dim.key]);
            if (Number.isNaN(value) || value < 0) {
                toast.error(`${dim.label} weight must be a non-negative number.`);
                return;
            }
            normalized[dim.key] = value;
        }

        const total = Math.round(Object.values(normalized).reduce((sum, v) => sum + v, 0) * 1000) / 1000;

        if (total > 1) {
            toast.error(`Weights sum to ${total}, which exceeds 1. Please reduce one or more values.`);
            return;
        }

        setSaving(true);
        try {
            await assessmentApi.updatePairwiseWeights(normalized);
            setWeights(normalized);
            setDraft(normalized);
            setEditing(false);
            toast.success('Pairwise weights updated.');
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || 'Failed to save weights.');
        } finally {
            setSaving(false);
        }

    };

    return (
        <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 4 }}>
                <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FontAwesomeIcon icon={faShuffle} style={{ color: 'var(--accent)' }} />
                        Pairwise Detail — Algorithm Weights (MinHash + LSH + Weighted Jaccard)
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                        Controls how much each metadata dimension contributes to the page-to-page similarity score used for Merge Sets / Overall Status.
                    </div>
                </div>

                {!loading && (
                    editing ? (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: weightsExceedOne ? 'var(--danger)' : 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                                Sum: {weightsSumRounded.toFixed(2)} / 1.00
                            </span>
                            <Button variant="secondary" size="sm" onClick={handleCancelEditing} disabled={saving}>Cancel</Button>
                            <Button variant="primary" size="sm" onClick={handleSave} loading={saving} disabled={weightsExceedOne}>Save</Button>
                        </div>
                    ) : (
                        <Button variant="secondary" size="sm" onClick={handleStartEditing}>Edit Weights</Button>
                    )
                )}
            </div>

            {error && (
                <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 10 }}>{error}</div>
            )}

            <div style={{ overflowX: 'auto', border: '0.5px solid var(--border)', borderRadius: 'var(--radius-md)', marginTop: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                        <tr>
                            {['Dimension', 'Weight', 'How it works'].map(h => (
                                <th key={h} style={configTableHeaderStyle}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={3} style={{ ...configTableCellStyle, textAlign: 'center', color: 'var(--text-tertiary)' }}>
                                    Loading...
                                </td>
                            </tr>
                        ) : PAIRWISE_DIMENSION_META.map(dim => (
                            <tr key={dim.key}>
                                <td style={configTableCellStyle}>{dim.label}</td>
                                <td style={{ ...configTableCellStyle, whiteSpace: 'nowrap' }}>
                                    {editing ? (
                                        <input
                                            type="number"
                                            min={0}
                                            max={1}
                                            step={0.01}
                                            value={draft[dim.key]}
                                            onChange={e => handleWeightChange(dim.key, e.target.value)}
                                            style={configInputStyle}
                                        />
                                    ) : (
                                        <span style={{ fontWeight: 600 }}>{Number(weights[dim.key] ?? 0).toFixed(2)}</span>
                                    )}
                                </td>
                                <td style={configTableCellStyle}>{dim.description}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {!loading && !editing && (
                <div style={{ marginTop: 10, fontSize: 11.5, color: weightsExceedOne ? 'var(--danger)' : 'var(--text-secondary)' }}>
                    Current sum of weights: {Math.round(Object.values(weights).reduce((sum, v) => sum + (Number(v) || 0), 0) * 1000) / 1000} / 1.00
                </div>
            )}

            <div style={{ marginTop: 12, fontSize: 11.5, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
                <strong>Recommendation:</strong> score &ge; 85 = MERGE, score &ge; 70 = REVIEW, below 70 = SEPARATE.<br />
                <strong>Weights</strong> must sum to 1.00 or less. Saving updates the backend (<code>PUT /assessment/weights</code>) and is used by the Assess page's Overall Status, Merge Sets, and Pairwise Detail views.
            </div>
        </Card>
    );
}

/*
|--------------------------------------------------------------------------
| Shared table/input styles
|--------------------------------------------------------------------------
*/

const configTableHeaderStyle = {
    textAlign: 'left',
    padding: '9px 12px',
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    borderBottom: '0.5px solid var(--border)',
    background: 'var(--bg-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '.03em'
};

const configTableCellStyle = {
    padding: '8px 12px',
    borderBottom: '0.5px solid var(--border)',
    color: 'var(--text-primary)',
    verticalAlign: 'top'
};

const configInputStyle = {
    width: 70,
    padding: '4px 6px',
    border: '0.5px solid var(--border-med)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 12,
    background: 'var(--bg-secondary)',
    color: 'var(--text-primary)'
};
